import type React from "react"
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"

export interface MentionCandidate {
	/** userId for humans; the AI actor id for Mareczek. */
	key: string
	kind: "user" | "ai"
	userId?: string
	/** Full name — shown in the dropdown + used for filtering. */
	displayName: string
	/** Display token inserted/rendered after the `@` (e.g. patryk.wojtyński). */
	handle: string
	email?: string
}

const MARECZEK_CANDIDATE: MentionCandidate = {
	key: "mareczek-ai",
	kind: "ai",
	displayName: "mareczek.ai",
	handle: "mareczek.ai",
}

const MAX_RESULTS = 8

function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * The `@token` immediately left of the caret, or null when not in a mention.
 * The query may contain spaces (display names are "First Last"); it runs to the
 * caret and stops only at a newline or another `@`. The picker self-limits —
 * once the query matches no candidate the dropdown closes.
 */
function activeQuery(
	value: string,
	caret: number,
): { start: number; query: string } | null {
	const before = value.slice(0, caret)
	const m = before.match(/(?:^|\s)@([^\n@]*)$/)
	if (!m) return null
	const query = m[1] ?? ""
	return { start: caret - query.length - 1, query }
}

/**
 * Drives an @-mention picker over a plain textarea (ZPL-145). Detects the
 * `@query` at the caret, filters the candidate people (Mareczek pinned on top
 * when the query is empty or matches `mareczek.ai`), handles keyboard
 * navigation, inserts `@Display Name`, and tracks the resolved picks so the
 * composer can send `{ userIds, mareczek }` on submit. The server re-validates.
 */
export function useMentionCompose({
	people,
	draft,
	setDraft,
	textareaRef,
	includeMareczek = true,
}: {
	people: MentionCandidate[]
	draft: string
	setDraft: (v: string) => void
	textareaRef: React.RefObject<HTMLTextAreaElement | null>
	includeMareczek?: boolean
}) {
	const [query, setQuery] = useState<string | null>(null)
	const [activeIndex, setActiveIndex] = useState(0)
	// Resolved picks keyed by lowercased display name, so deleting the `@name`
	// from the draft before sending drops the mention.
	const selectedRef = useRef<Map<string, MentionCandidate>>(new Map())
	// Caret position to restore after an insertion, or null when there is
	// nothing pending.
	const pendingCaretRef = useRef<number | null>(null)

	/**
	 * Put the caret after the handle we just inserted.
	 *
	 * This has to wait for React to write the new draft into the textarea —
	 * setting the range before the commit would be clobbered by it — but it
	 * must NOT wait a whole frame. `requestAnimationFrame` (the previous
	 * approach) lets the browser run other input in between, and a caret that
	 * lands mid-interaction silently collapses a pending selection: an
	 * insert-over-selection then appends instead of replacing. A layout effect
	 * runs synchronously after the same commit, so no other input can
	 * interleave.
	 */
	useLayoutEffect(() => {
		const pos = pendingCaretRef.current
		if (pos === null) return
		pendingCaretRef.current = null
		const el = textareaRef.current
		if (!el) return
		el.focus()
		el.setSelectionRange(pos, pos)
	})

	const candidates = useMemo<MentionCandidate[]>(() => {
		if (query === null) return []
		const q = query.toLowerCase()
		const humans = people.filter(
			(c) =>
				q === "" ||
				c.displayName.toLowerCase().includes(q) ||
				c.handle.includes(q) ||
				(c.email?.toLowerCase().includes(q) ?? false),
		)
		// Pin Mareczek when the query is empty or is a prefix of its handle —
		// not when the query merely appears somewhere inside "mareczek".
		const showMareczek =
			includeMareczek && (q === "" || "mareczek.ai".startsWith(q))
		const list = showMareczek ? [MARECZEK_CANDIDATE, ...humans] : humans
		return list.slice(0, MAX_RESULTS)
	}, [query, people, includeMareczek])

	const open = query !== null && candidates.length > 0

	const close = useCallback(() => setQuery(null), [])

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.target.value
			const caret = e.target.selectionStart ?? value.length
			setDraft(value)
			const tok = activeQuery(value, caret)
			setQuery(tok ? tok.query : null)
			setActiveIndex(0)
		},
		[setDraft],
	)

	// Reopen/close as the caret moves (arrow keys, click) without text change.
	const onCaretMove = useCallback(() => {
		const el = textareaRef.current
		if (!el) return
		const caret = el.selectionStart ?? el.value.length
		const tok = activeQuery(el.value, caret)
		setQuery((prev) => {
			const next = tok ? tok.query : null
			if (next !== prev) setActiveIndex(0)
			return next
		})
	}, [textareaRef])

	const select = useCallback(
		(c: MentionCandidate) => {
			const el = textareaRef.current
			if (!el) return
			const caret = el.selectionStart ?? draft.length
			const tok = activeQuery(draft, caret)
			if (!tok) return
			const insert = `@${c.handle} `
			const next = draft.slice(0, tok.start) + insert + draft.slice(caret)
			// Key by the durable id, not the display name — two members can share
			// a display name and must not collapse to one pick.
			selectedRef.current.set(c.key, c)
			setDraft(next)
			setQuery(null)
			// Applied in the layout effect below, once React has written the new
			// value to the DOM.
			pendingCaretRef.current = tok.start + insert.length
		},
		[draft, setDraft, textareaRef],
	)

	/** Returns true when the picker consumed the key (composer must not submit). */
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent): boolean => {
			if (!open) return false
			if (e.key === "ArrowDown") {
				e.preventDefault()
				setActiveIndex((i) => (i + 1) % candidates.length)
				return true
			}
			if (e.key === "ArrowUp") {
				e.preventDefault()
				setActiveIndex((i) => (i - 1 + candidates.length) % candidates.length)
				return true
			}
			if (e.key === "Enter" || e.key === "Tab") {
				const c = candidates[activeIndex]
				if (c) {
					e.preventDefault()
					select(c)
					return true
				}
			}
			if (e.key === "Escape") {
				e.preventDefault()
				close()
				return true
			}
			return false
		},
		[open, candidates, activeIndex, select, close],
	)

	const resolveMentions = useCallback(
		(finalText: string): { userIds: string[]; mareczek: boolean } => {
			const userIds: string[] = []
			let mareczek = false
			for (const c of selectedRef.current.values()) {
				// Match the inserted "@handle" as a whole token (bounded by
				// start/space and a trailing space or end) so a selected handle that
				// is a substring of other text (e.g. "@sam" within "@samples", or a
				// shorter handle prefixing a longer one) never yields a false mention.
				const re = new RegExp(
					`(?:^|\\s)@${escapeRegExp(c.handle)}(?=\\s|$)`,
					"i",
				)
				if (!re.test(finalText)) continue
				if (c.kind === "ai") mareczek = true
				else if (c.userId) userIds.push(c.userId)
			}
			return { userIds: [...new Set(userIds)], mareczek }
		},
		[],
	)

	const reset = useCallback(() => {
		selectedRef.current.clear()
		setQuery(null)
		setActiveIndex(0)
	}, [])

	const setSelectedCandidates = useCallback((items: MentionCandidate[]) => {
		selectedRef.current = new Map(items.map((item) => [item.key, item]))
		setQuery(null)
		setActiveIndex(0)
	}, [])

	return {
		open,
		candidates,
		activeIndex,
		setActiveIndex,
		onChange,
		onKeyDown,
		onCaretMove,
		select,
		resolveMentions,
		reset,
		setSelectedCandidates,
	}
}
